import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Alert, Box, Button, Container, Group, Image, List, Loader, Text, ThemeIcon, Title } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useColors } from '../hooks/useColors';
import { useTranslation } from '../hooks/useTranslation';
import classes from './HomePage.module.css';

// Define a local interface to use with type assertion
interface TranslationResult {
  t: (key: string, params?: Record<string, string>, defaultValue?: string) => string;
  changeLanguage: (lang: string) => Promise<void>;
  currentLanguage: string;
  isChangingLanguage: boolean;
  isLoaded: boolean;
}

export function HomePage() {
  const { isAuthenticated, login, loginWithRedirect } = useAuth();
  // Use type assertion to handle the isLoaded property
  const translationHook = useTranslation() as TranslationResult;
  const { t, isLoaded } = translationHook;

  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [searchParams] = useSearchParams();
  const colors = useColors();

  // Check if user was redirected due to session expiration
  const sessionExpired = searchParams.get('sessionExpired') === 'true';

  // Add a safety timeout to ensure we don't get stuck loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoaded) {
        setLoadingTimeout(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isLoaded]);

  // On session expiry, auto-trigger loginRedirect after a short delay so the
  // user sees the alert. On domain computers Azure AD SSO completes instantly.
  useEffect(() => {
    if (!sessionExpired || isAuthenticated) return;

    const timer = setTimeout(() => {
      loginWithRedirect();
    }, 3000);

    return () => clearTimeout(timer);
    // loginWithRedirect is stable (useCallback) — safe to include
  }, [sessionExpired, isAuthenticated, loginWithRedirect]);

  // If already authenticated, redirect to CVs page
  if (isAuthenticated) {
    return <Navigate to="/cvs" replace />;
  }

  // Show loading state while translations are being loaded
  if (!isLoaded && !loadingTimeout) {
    return (
      <Container size="lg" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader size="xl" />
      </Container>
    );
  }

  // Custom gradient style for checkboxes
  const gradientIconStyle = {
    background: colors.gradients.HOMEPAGE,
  };

  // Get translations with fallbacks
  const titleText = t('HOME_TITLE', {}, 'Create Professional CVs with Ease');
  const highlightText = t('HOME_HIGHLIGHT', {}, 'Professional');
  const descriptionText = t('HOME_DESCRIPTION', {}, 'Our platform makes it simple to create, manage, and share your CV with potential employers.');

  return (
    <Container size="lg">
      {sessionExpired && (
        <Alert
          icon={<ExclamationTriangleIcon className="w-5 h-5" />}
          title="Session Expired"
          color="orange"
          mb="md"
        >
          Your session has expired due to inactivity. Please sign in again to continue.
        </Alert>
      )}
      <div className={classes.inner}>
        <div className={classes.content}>
          <Title className={classes.title}>
            {titleText.split(highlightText).map((part: string, index: number, array: string[]) => {
              // For the last part, don't add the highlight span
              if (index === array.length - 1) return part;
              // Otherwise add part + highlight + space
              return (
                <React.Fragment key={index}>
                  {part}<span className={classes.highlight}>{highlightText}</span>{' '}
                </React.Fragment>
              );
            })}
          </Title>
          <Text className={classes.description}>
            {descriptionText}
          </Text>

          <List
            mt={30}
            spacing="sm"
            size="sm"
            icon={
              <Box style={gradientIconStyle} className={classes.gradientIcon}>
                <ThemeIcon size={20} radius="xl" variant="transparent">
                  <CheckIcon width={12} height={12} color="white" />
                </ThemeIcon>
              </Box>
            }
          >
            <List.Item>
              <b>{t('HOME_FEATURE_1', {}, 'Multiple Templates')}</b> – {t('HOME_FEATURE_1_DESC', {}, 'Choose from a variety of professional templates')}
            </List.Item>
            <List.Item>
              <b>{t('HOME_FEATURE_2', {}, 'Export Options')}</b> – {t('HOME_FEATURE_2_DESC', {}, 'Download as PDF, Word, or share online')}
            </List.Item>
            <List.Item>
              <b>{t('HOME_FEATURE_3', {}, 'Real-time Editing')}</b> – {t('HOME_FEATURE_3_DESC', {}, 'See changes as you make them')}
            </List.Item>
          </List>

          <Group mt={30}>
            <Button
              className={classes.gradientButton}
              radius="xl"
              size="md"
              onClick={login}
            >
              {t('HOME_SIGN_IN', {}, 'Sign In')}
            </Button>
          </Group>
        </div>
        <Image
          src="./hero.jpg"
          className={classes.image}
          alt="CV Portal Illustration"
        />
      </div>
    </Container>
  );
} 